import * as React from "react";
import "../filters.scss";
import {none, option, Option} from "ts-option";
import RadioButton, {IRadioButton} from "../../../../hiberUiKit/src/atoms/Buttons/RadioButton";
import OptionSelector from "../../../../hiberUiKit/src/molecules/OptionSelector/OptionSelector";
import {DateFilterType, DateFilterTypeName, unitsOfTime} from "./DateFilter";
import NumberUtils from "../../../../utils/NumberUtils";
import DateFilterModel from "../../../../model/workspace/DateFilterModel";
import {DateFilterValueItemApiDto} from "../../../../api/model/rest";
import moment = require("moment");
import DurationConstructor = moment.unitOfTime.DurationConstructor;

interface IProps {
	/**
	 * Input value
	 */
	value: Option<UpToFilterModel>
	/**
	 * On change callback
	 * @param {Option<UpToFilterModel>} value
	 */
	onChange: (value: Option<UpToFilterModel>) => void
	/**
	 * Selected radio button Id
	 */
	selectedRadioButtonId: string
	/**
	 * On select button
	 * @param {string} id
	 */
	onSelectButton: (id: string) => void
}

interface IState {
	/**
	 * Selected unit of time for "Up to"
	 */
	selectedUnitOfTime: DurationConstructor
	/**
	 * Represents input value for "Up to" (end date in future)
	 * Start date represents this moment
	 */
	inputEndValue: string;
}

/**
 * Component that allows filtering with use of date from future
 * Includes dates up to the specified time
 */
export default class UpToFilter extends React.Component<IProps, IState> {

	radioButton: IRadioButton = {
		label: DateFilterTypeName[DateFilterType.UpTo],
		disabled: false,
		value: DateFilterType.UpTo
	};

	constructor(props: IProps) {
		super(props);
		this.state = {
			inputEndValue: props.value.map(v => v.timeValue.toString()).getOrElse(""),
			selectedUnitOfTime: props.value.map(v => v.unitOfTime).getOrElse("minutes"),
		};
		this.onChangeStartInput = this.onChangeStartInput.bind(this);
		this.onChangeSelector = this.onChangeSelector.bind(this);
		this.onChangeCallback = this.onChangeCallback.bind(this);
		this.resetFilter = this.resetFilter.bind(this);
	}

	componentWillReceiveProps(nextProps: IProps) {
		if (this.props.selectedRadioButtonId === this.radioButton.value &&
			nextProps.selectedRadioButtonId !== this.radioButton.value) {
			this.resetFilter();
		}
	}

	render() {
		const radioButtonChecked = (this.props.selectedRadioButtonId === this.radioButton.value);
		return (
			<div className={"c-filter-dates_inputs_row"}>
				<RadioButton radioButton={this.radioButton}
							 onChange={() => this.props.onSelectButton(this.radioButton.value)}
							 checked={radioButtonChecked}
							 onFocus={this.resetFilter}/>
				<input className={"c-filter-dates_input_up-to"}
					   type="number"
					   value={this.state.inputEndValue}
					   onChange={this.onChangeStartInput}
					   disabled={!radioButtonChecked}/>
				<OptionSelector selectableOptions={unitsOfTime}
								onSelect={this.onChangeSelector}
								disabled={!radioButtonChecked}
								selectedOption={this.state.selectedUnitOfTime}/>
			</div>
		);
	}

	/**
	 * On change in "Up to" input
	 */
	onChangeStartInput(e: React.FormEvent<HTMLInputElement>) {
		if (e.currentTarget.value.length == 0 || NumberUtils.isNumber(e.currentTarget.value)) {
			this.setState({inputEndValue: e.currentTarget.value}, this.onChangeCallback)
		}
	}

	/**
	 * On change in "Up to" selector
	 */
	onChangeSelector(selectedUnitOfTime: DurationConstructor) {
		this.setState({selectedUnitOfTime}, this.onChangeCallback);
	}

	/**
	 * On any change callback
	 */
	onChangeCallback() {
		const timeValue = this.state.inputEndValue;
		const unitOfTime = this.state.selectedUnitOfTime;

		if (timeValue && unitOfTime) {
			this.props.onChange(option(new UpToFilterModel(Number(timeValue), unitOfTime)));
		}
		else {
			this.props.onChange(none);
		}
	}

	/**
	 * Reset filter
	 */
	resetFilter() {
		this.setState({inputEndValue: ""});
	}
}

export class UpToFilterModel extends DateFilterModel {
	constructor(public readonly timeValue: number,
		public readonly unitOfTime: DurationConstructor) {
		super(DateFilterType.UpTo)
	}

	static fromJson(json: DateFilterValueItemApiDto[]) {
		return new UpToFilterModel(json[0].value, json[0].rangeUnit.toLowerCase() as DurationConstructor)
	}

	getDisplayValue(): string {
		return `${super.getDisplayValue()} ${this.timeValue} ${this.unitOfTime}`;
	}

	toQueryJson(): (DateFilterValueItemApiDto | string)[] {
		const currentDate = moment().toISOString();
		return [{value: this.timeValue, rangeUnit: this.unitOfTime.toUpperCase()}, currentDate]
	}
}
